import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchCurrentUser, roleHomePath } from "../lib/auth";

type RequireAuthProps = {
  children: ReactNode;
  allowedRoles?: string[];
};

export function RequireAuth({ children, allowedRoles }: RequireAuthProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<"loading" | "allowed" | "blocked">("loading");

  useEffect(() => {
    let ignore = false;

    async function loadUser() {
      try {
        const data = await fetchCurrentUser();
        if (ignore) {
          return;
        }

        const user = data.user;
        if (!data.authenticated || !user) {
          navigate("/auth/login", {
            replace: true,
            state: { from: location.pathname }
          });
          return;
        }

        if (allowedRoles && !allowedRoles.includes(user.role || "")) {
          navigate(roleHomePath(user.role), { replace: true });
          return;
        }

        setState("allowed");
      } catch {
        if (!ignore) {
          navigate("/auth/login", {
            replace: true,
            state: { from: location.pathname }
          });
        }
      }
    }

    void loadUser();

    return () => {
      ignore = true;
    };
  }, [allowedRoles, location.pathname, navigate]);

  if (state !== "allowed") {
    return (
      <div className="grid min-h-[40vh] place-items-center text-sm text-current/70">
        Checking your session...
      </div>
    );
  }

  return <>{children}</>;
}
